/**
 * Versioned tax year configuration for Claim tax back.
 *
 * IMPORTANT: This is people's money. Bands and thresholds must match the year
 * being CLAIMED, not the current year. getClaimTaxYearConfig() throws for
 * years we have not configured rather than silently reusing another year's
 * figures.
 *
 * Note: the V1 prototype used the 2024/25 Scottish basic/intermediate split
 * (£26,561). The published 2025/26 split is £27,491/£27,492 - the figures
 * below follow GOV.UK, not the prototype.
 */

import type { ClaimIncomeBand, ClaimTaxRegion } from '@/types/claimTaxBack'

export type ClaimTaxYearConfig = {
  /** UK tax year period key this config applies to, e.g. "2025-2026". */
  periodKey: string
  /** Donor-facing year label, e.g. "2025/26". */
  yearLabel: string
  /** Checker income bands per region, in ascending income order. */
  bands: Record<ClaimTaxRegion, ClaimIncomeBand[]>
  /**
   * Basic rate of income tax as a whole-number percent. Gift Aid is always
   * repaid to charities at this UK-wide rate; donor relief is anything above it.
   */
  basicRatePercent: number
  /** PAYE donors can claim by phone when total GIVEN is at or under this, in pence. */
  payePhoneMaxGivenPence: number
  /** PAYE donors must list every donation when total GIVEN reaches this, in pence. */
  payeItemisedFromGivenPence: number
  /** HMRC income tax helpline used for phone claims. */
  hmrcPhoneNumber: string
  /** Opening hours shown next to the helpline number. */
  hmrcPhoneHours: string
  /** Where written claims are sent. */
  hmrcPostalAddress: string[]
  /** Where these figures came from. */
  sources: string[]
}

const POUND = 100

/**
 * 2025/26: the tax year 6 Apr 2025 to 5 Apr 2026, the previous completed year
 * as of the V1 build. Boundaries are total annual income assuming the standard
 * personal allowance (£12,570), which is how the checker asks the question.
 */
const CONFIG_2025_2026: ClaimTaxYearConfig = {
  periodKey: '2025-2026',
  yearLabel: '2025/26',
  basicRatePercent: 20,
  bands: {
    // England, Wales and Northern Ireland.
    // Source: https://www.gov.uk/income-tax-rates/previous-tax-years
    ruk: [
      {
        id: 'ruk-basic',
        label: '£50,270 or less',
        marginalRatePercent: 20,
        fromPence: 0,
        toPence: 50_270 * POUND,
      },
      {
        id: 'ruk-higher',
        label: '£50,271 to £125,140',
        marginalRatePercent: 40,
        // Bounds partition income space penny-to-penny; labels keep HMRC's
        // published whole-pound wording.
        fromPence: 50_270 * POUND + 1,
        toPence: 125_140 * POUND,
      },
      {
        id: 'ruk-additional',
        label: 'Over £125,140',
        marginalRatePercent: 45,
        fromPence: 125_140 * POUND + 1,
        toPence: null,
      },
    ],
    // Scotland. Starter (19%) and basic (20%) are grouped: neither is owed
    // extra relief. Intermediate (21%) is kept separate: those donors are owed
    // 1 percentage point and we tell them (product decision from requirements).
    // Source: https://www.gov.uk/scottish-income-tax/2025-to-2026-tax-year
    scotland: [
      {
        id: 'scotland-basic',
        label: '£27,491 or less',
        marginalRatePercent: 20,
        fromPence: 0,
        toPence: 27_491 * POUND,
      },
      {
        id: 'scotland-intermediate',
        label: '£27,492 to £43,662',
        marginalRatePercent: 21,
        fromPence: 27_491 * POUND + 1,
        toPence: 43_662 * POUND,
      },
      {
        id: 'scotland-higher',
        label: '£43,663 to £75,000',
        marginalRatePercent: 42,
        fromPence: 43_662 * POUND + 1,
        toPence: 75_000 * POUND,
      },
      {
        id: 'scotland-advanced',
        label: '£75,001 to £125,140',
        marginalRatePercent: 45,
        fromPence: 75_000 * POUND + 1,
        toPence: 125_140 * POUND,
      },
      {
        id: 'scotland-top',
        label: 'Over £125,140',
        marginalRatePercent: 48,
        fromPence: 125_140 * POUND + 1,
        toPence: null,
      },
    ],
  },
  // HMRC claim route thresholds are on the amount GIVEN (net), not the Gift
  // Aided total. Source: https://www.gov.uk/donating-to-charity/gift-aid
  payePhoneMaxGivenPence: 5_000 * POUND,
  payeItemisedFromGivenPence: 10_000 * POUND,
  hmrcPhoneNumber: '0300 200 3300',
  hmrcPhoneHours: 'Monday to Friday, 8am to 6pm',
  hmrcPostalAddress: [
    'Pay As You Earn and Self Assessment',
    'HM Revenue and Customs',
    'BX9 1AS',
    'United Kingdom',
  ],
  sources: [
    'https://www.gov.uk/income-tax-rates/previous-tax-years',
    'https://www.gov.uk/scottish-income-tax/2025-to-2026-tax-year',
    'https://www.gov.uk/donating-to-charity/gift-aid',
  ],
}

const CONFIGS_BY_PERIOD: Record<string, ClaimTaxYearConfig> = {
  [CONFIG_2025_2026.periodKey]: CONFIG_2025_2026,
}

/** Period key of the tax year V1 supports claiming for. */
export const V1_CLAIMABLE_PERIOD_KEY = CONFIG_2025_2026.periodKey

/**
 * Sanity cap the API puts on manualGivingPence: £10m in pence. Above this the
 * real API returns 400, so the checker must stop first with a friendly message.
 */
export const MAX_MANUAL_GIVING_PENCE = 1_000_000_000

/**
 * Returns the verified config for a tax year. Throws for unconfigured years so
 * we can never show a donor another year's bands by accident.
 */
export function getClaimTaxYearConfig(periodKey: string): ClaimTaxYearConfig {
  const config = CONFIGS_BY_PERIOD[periodKey]
  if (!config) {
    throw new Error(
      `No claim tax back config for tax year "${periodKey}". ` +
        'Figures must be verified per year before donors can claim for it.'
    )
  }
  return config
}

/** Finds a checker band by its stable id across both regions, or null. */
export function findIncomeBandById(
  config: ClaimTaxYearConfig,
  bandId: string
): ClaimIncomeBand | null {
  const allBands = [...config.bands.ruk, ...config.bands.scotland]
  return allBands.find((band) => band.id === bandId) ?? null
}

/**
 * Extra Gift Aid relief is only due when the donor's marginal rate is above
 * the UK basic rate. Basic-rate (and Scottish starter/basic grouped) bands
 * mean there is nothing extra for them to claim from HMRC.
 */
export function bandQualifiesForExtraRelief(
  band: ClaimIncomeBand,
  config: ClaimTaxYearConfig
): boolean {
  return band.marginalRatePercent > config.basicRatePercent
}
